/**
 * Settings > Notifications > History (tracker item 35): every queued message,
 * newest first, filterable by status; retry failed, cancel queued. ADMIN and
 * OFFICE_MANAGER (backend requireRole('ADMIN', 'OFFICE_MANAGER')).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient, INotificationsApi } from '../../api';
import * as types from '../../api/types';
import { getErrorMessage } from '../../utils/apiError';
import { inputClass, labelClass, rowActionClass, rowDangerActionClass, secondaryButtonClass } from '../../components/formStyles';
import { OutboxStatusBadge } from '../../components/NotificationBadges';
import { DELIVERY_LABELS, OUTBOX_STATUS_LABELS, eventLabel } from './notificationUi';
import { Toast } from '../../components/Toast';

const PAGE_SIZE = 50;

export function NotificationHistoryPage() {
  const api: INotificationsApi = useMemo(() => createAPIClient(), []);
  const [status, setStatus] = useState<types.OutboxStatus | ''>('');
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<types.PaginatedResponse<types.OutboxMessage> | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setResult(await api.listOutbox({ status }, page, PAGE_SIZE));
      setError(null);
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not load message history'));
    } finally {
      setLoading(false);
    }
  }, [api, status, page]);

  useEffect(() => { void load(); }, [load]);

  async function act(id: string, action: () => Promise<unknown>) {
    setBusyId(id);
    setError(null);
    try {
      await action();
      await load();
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not update the message'));
    } finally {
      setBusyId(null);
    }
  }

  const rows = result?.data ?? [];
  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <Link to="/settings/notifications" className="text-sm text-blue-700 hover:underline">← Notifications</Link>
      <h1 className="text-2xl font-bold text-gray-900 mt-1 mb-5">Message history</h1>
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end gap-3 mb-4">
        <div className="w-full sm:w-auto">
          <label htmlFor="outbox-status" className={labelClass}>Status</label>
          <select id="outbox-status" className={inputClass} value={status}
            onChange={(e) => { setStatus(e.target.value as types.OutboxStatus | ''); setPage(1); }}>
            <option value="">All</option>
            {types.OUTBOX_STATUSES.map((s) => <option key={s} value={s}>{OUTBOX_STATUS_LABELS[s]}</option>)}
          </select>
        </div>
        <button type="button" className={secondaryButtonClass} onClick={() => void load()} disabled={loading}>Refresh</button>
      </div>
      {error && <Toast kind="error">{error}</Toast>}
      {loading && !result ? (
        <p>Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-gray-600">No messages.</p>
      ) : (
        <div className="md:overflow-x-auto md:bg-white md:rounded-lg md:shadow">
          <table className="responsive-table min-w-full text-sm">
            <thead>
              <tr className="border-b text-left">
                {['Queued', 'Message', 'Customer', 'To', 'Status', 'Details', ''].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => (
                <React.Fragment key={m.id}>
                  <tr className="border-b align-top">
                    <td data-label="Queued" className="px-3 py-2 whitespace-nowrap">{new Date(m.createdAt).toLocaleString()}</td>
                    <td data-label="Message" data-primary className="px-3 py-2">
                      <div className="min-w-0">
                        <button type="button" className={`${rowActionClass} text-left`} onClick={() => setOpen(open === m.id ? null : m.id)}>
                          {eventLabel(m.eventKey)}
                        </button>
                        {m.subject && <p className="text-gray-600 font-normal text-sm break-words">{m.subject}</p>}
                      </div>
                    </td>
                    <td data-label="Customer" className="px-3 py-2">
                      {m.customerId ? <Link className="text-blue-700 hover:underline" to={`/customers/${m.customerId}`}>{m.customerName ?? 'Customer'}</Link> : '—'}
                    </td>
                    <td data-label="To" className="px-3 py-2 break-all md:break-normal">{m.toAddress ?? '—'}</td>
                    <td data-label="Status" className="px-3 py-2"><OutboxStatusBadge status={m.status} /></td>
                    <td data-label="Details" className="px-3 py-2 text-gray-700">
                      <div className="min-w-0 break-words">
                        {m.skipReasonText ?? m.lastError ?? (m.deliveryStatus ? DELIVERY_LABELS[m.deliveryStatus] : m.sentAt ? `Sent ${new Date(m.sentAt).toLocaleString()}` : '')}
                        {m.attempts > 0 && m.status !== 'SENT' && <span className="block text-xs text-gray-500">Attempts: {m.attempts}</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {m.status === 'FAILED' && (
                        <button type="button" className={`${rowActionClass} min-h-[44px] md:min-h-0`} disabled={busyId === m.id}
                          onClick={() => void act(m.id, () => api.retryOutboxMessage(m.id))}>Retry</button>
                      )}
                      {m.status === 'PENDING' && (
                        <button type="button" className={`${rowDangerActionClass} min-h-[44px] md:min-h-0`} disabled={busyId === m.id}
                          onClick={() => void act(m.id, () => api.cancelOutboxMessage(m.id))}>Cancel</button>
                      )}
                    </td>
                  </tr>
                  {open === m.id && (
                    <tr className="border-b bg-gray-50">
                      <td colSpan={7} className="px-3 py-3"><pre className="whitespace-pre-wrap break-words font-sans text-gray-800">{m.body}</pre></td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {result && result.pagination.pages > 1 && (
        <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
          <button type="button" className={secondaryButtonClass} disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
          <span>Page {result.pagination.page} of {result.pagination.pages}</span>
          <button type="button" className={secondaryButtonClass} disabled={page >= result.pagination.pages} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      )}
    </main>
  );
}
